import { Button as FloraButton } from '@grupoboticario/flora-react';
import { ButtonIcon } from 'ui/components/primitives/Button';
import { theme, styled, utils } from 'ui/theme';

export const slideSizeCSS = '--slide-size';
export const offsetCSS = '--offset';

export const Navigation = {
  Content: styled(FloraButton)`
    align-items: center;
    background: ${theme.colors.action.navigation.alt.static};
    box-shadow: 0 0 0 1px ${theme.colors.action.navigation.standard.static};
    color: ${theme.colors.action.navigation.standard.static};
    height: ${theme.space[6]};
    width: ${theme.space[6]};
    padding: ${theme.space[0]};

    &[data-size='lg'] {
      height: ${theme.space[10]};
      width: ${theme.space[12]};
    }

    &:hover {
      background: ${theme.colors.action.navigation.standard.static};
      color: ${theme.colors.build.lightDark2};
    }
  `,

  Wrapper: styled('div')`
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    height: ${theme.space[10]};
    width: ${theme.space[10]};

    &:first-of-type {
      p {
        transform: translate3d(calc(-1 * ${theme.space[4]}), 0px, 0px);
      }
    }

    &:last-of-type {
      p {
        transform: translate3d(${theme.space[4]}, 0px, 0px);
      }
    }

    p {
      width: 0;
      opacity: 0;
      ${utils.transition('all').easing('easeInOut').duration('shortest')};
    }

    &[data-desktop='false'] {
      padding: ${theme.space[1]} 0;

      &[data-container='false'] {
        &:first-of-type {
          left: calc(-1 * ${theme.space[1]});
        }

        &:last-of-type {
          right: calc(-1 * ${theme.space[1]});
        }
      }

      &[data-container='true'] {
        &:first-of-type {
          left: ${theme.space[1]};
          width: initial;
        }

        &:last-of-type {
          right: ${theme.space[1]};
          width: initial;
        }
      }
    }

    &[data-desktop='true'] {
      height: ${theme.space[23]};

      &[data-container='false'] {
        &:first-of-type {
          left: calc(-1 * ${theme.space[2]});

          &[data-size='sm'] {
            left: calc(-0 * ${theme.space[1]});
          }
        }

        &:last-of-type {
          right: calc(-1 * ${theme.space[2]});

          &[data-size='sm'] {
            right: calc(-0 * ${theme.space[1]});
          }
        }
      }

      &[data-container='true'] {
        &:first-of-type {
          left: ${theme.space[1]};
        }

        &:last-of-type {
          right: ${theme.space[1]};
        }

        ${utils.media.min('xl')`
          &:first-of-type {
            left: calc(-1 * ${theme.space[10]});
          }
      
          &:last-of-type {
            right: calc(-1 * ${theme.space[10]});
          }

          &[data-size='lg'] {
            &:first-of-type {
              left: calc(-1 * ${theme.space[12]});
            }
        
            &:last-of-type {
              right: calc(-1 * ${theme.space[12]});
            }
          }
        `}
      }
    }

    &[data-disabled='true'] {
      button {
        background: ${theme.colors.action.navigation.alt.disabled};
        box-shadow: 0 0 0 1px ${theme.colors.action.navigation.standard.disabled};
        color: ${theme.colors.action.navigation.standard.disabled};
        cursor: not-allowed;
      }
    }

    &[data-desktop='true'] {
      &[data-disabled='false'] {
        &:hover {
          width: initial;

          button {
            background: ${theme.colors.action.navigation.standard.highlight};
            box-shadow: 0 0 0 1px ${theme.colors.action.navigation.standard.highlight};
            color: ${theme.colors.action.navigation.alt.static};
            width: initial;
            max-width: initial;

            svg {
              width: ${theme.sizes[6]};
            }
          }

          p {
            width: auto;
            opacity: 1;
            transform: translate3d(0, 0px, 0px);
          }

          &:first-of-type {
            p {
              padding-right: ${theme.space[2]};
            }

            &[data-size='sm'] {
              margin-left: ${theme.space[2]};
            }
          }

          &:last-of-type {
            p {
              padding-left: ${theme.space[2]};
            }

            &[data-size='sm'] {
              margin-right: ${theme.space[2]};
            }
          }
        }
      }
    }
  `,
};

export const Pagination = styled.div`
  background: transparent;
  display: flex;
  justify-content: center;
  margin-top: ${theme.space[4]};
  ${utils.media.max('s')`
    transform: scale(0.7);
  `}
`;

export const Page = styled.div`
  ${utils.transition('all').easing('easeInOut').duration('shortest')};
  background: transparent;
  border-radius: ${theme.sizes[4]};
  border: none;
  box-shadow: 0 0 0 1px ${theme.colors.action.navigation.standard.static};
  height: ${theme.sizes[2]};
  margin: 0 ${theme.sizes[2]};
  padding: 0;
  width: ${theme.sizes[2]};

  &:hover {
    background: ${theme.colors.action.navigation.standard.static};
  }

  &[data-active='true'] {
    background: ${theme.colors.action.navigation.standard.highlight};
    box-shadow: 0 0 0 1px ${theme.colors.action.navigation.standard.highlight};
    opacity: 1;
    width: ${theme.sizes[7]};
  }
`;

export const PageWrapper = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: ${theme.space[1]};
`;

export const Stage = styled.div`
  display: flex;
  padding: ${theme.space[1]};

  &[data-selectable='true'] {
    margin: ${theme.space[1]};
  }

  cursor: pointer;
`;

export const Slide = styled.div`
  border-radius: ${theme.radii.small};
  flex: 0 0 var(${slideSizeCSS});

  &:not([data-items='1']) {
    padding-right: ${theme.space[2]};
    flex: 0 0 calc(var(${slideSizeCSS}) - ${theme.space[3]});

    &:nth-last-of-type(1) {
      margin-right: 0;
    }
  }

  & > * {
    color: ${theme.colors.build.darkLight};
    width: 100%;
  }

  a {
    text-decoration: none;

    &:hover {
      text-decoration: none;
    }
  }

  &[data-selectable='true'] {
    color: ${theme.colors.build.darkLight};

    a {
      text-decoration: none;
    }

    &[data-active='true'] {
      box-shadow: 0 0 0 1px ${theme.colors.action.navigation.standard.static};
    }

    &[data-opacity='25'] {
      opacity: 0.25;
    }

    &[data-opacity='100'] {
      opacity: 1;
    }
  }

  &[data-product-list] {
    > div {
      ${utils.media.max('s')`
        height: 100%;
      `}
    }
    [data-quantity-selector] {
      margin-top: 16px;
    }
  }
`;
